// IntegrationSocial.js
// Purpose: Manages social media integrations (e.g., Twitter, LinkedIn, Facebook) for the workspace.
// Logic: Uses React hooks and state to handle authentication, display integration status, and manage related modals and forms.

import { useEffect, useState } from "react";
import { Accordion } from "flowbite-react";
import useCommonApi from "../hooks/common-apis";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { clog, isUserOwnerOfWorkspace } from "../lib/utils";

function IntegrationSocial() {
  const navigate = useNavigate();
  const [twitterProfile, setTwitterProfile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");

  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };

  const { checkTwitterAccess } = useCommonApi();
  let login_data = JSON.parse(localStorage.getItem("login_data"));

  async function handleTwitterLogin() {
    try {
      window.open(
        `${process.env.REACT_APP_BASE_USER_URL}/auth/twitter/${
          login_data?.resp_user?.id
        }?redirect_uri=${window.location.origin + window.location.pathname}`,
        "_self"
      );
    } catch (error) {
      console.error("Error initiating Twitter login", error);
    }
  }

  const integrateLinkedIn = async () => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/linkedin/auth`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        localStorage.setItem("linkedin_navigation", "/integration-social");
        window.open(response.data.url, "_self");
      } else {
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    }
  };

  const getLinkedInAccessToken = async (code) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/linkedin/access`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        localStorage.setItem("linkedin_access", JSON.stringify(response.data));
      } else {
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    } finally {
    }
  };

  useEffect(() => {
    // Check if user is workspace owner
    if (!isUserOwnerOfWorkspace()) {
      navigate("/unauthorized");
      return;
    }

    if (!twitterProfile) {
      checkTwitterAccess(setTwitterProfile, setChecking);
    }
    const searchParams = new URLSearchParams(window.location.search);
    let success = searchParams.get("success");
    //parse success to bool
    success = success === "true" ? true : false;
    const error = searchParams.get("error");
    //if searchparams are present then update the twitter access token
    if (searchParams.has("success")) {
      if (!success) {
        clog("error from query", error);
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(error);
      }
    }
  }, [twitterProfile]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let error = searchParams.get("error");
    if (error) {
      setopenFailureModal("dismissible");
      setsuccessFailureMessage(error);
      // remove error from search params after showing error using react router
      setTimeout(() => {
        searchParams.delete("error");
        window.history.replaceState({}, "", `${window.location.pathname}`);
      }, 500);
    }
  }, []);

  return (
    <>
      <h6 className="font-[600] text-[24px]">Social</h6>
      <p className="font-[500] text-[16px] pt-3">
        Connect your social media accounts to enable multi-channel outreach
      </p>

      {checking ? (
        // Skeleton layout while integrations are being checked
        <div className="my-[36px] integrationAccordion">
          <div className="mb-6">
            <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave mb-2"></div>
            <div className="h-4 w-64 bg-gray-300 rounded skeleton-wave"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(2)].map((_, index) => (
              <div
                key={index}
                className="btnBox p-4 rounded-[20px] border border-[#E9EBF0] bg-[#FAFBFC]"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gray-300 skeleton-wave"></div>
                  <div className="h-4 w-24 bg-gray-300 rounded skeleton-wave"></div>
                </div>
                <div className="h-4 w-32 bg-gray-300 rounded skeleton-wave mb-2"></div>
                <div className="h-8 w-20 bg-gray-300 rounded-full skeleton-wave mt-2 ml-auto"></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="my-[36px] integrationAccordion">
          <Accordion className="w-full mb-4">
            <Accordion.Panel>
              <Accordion.Title className="dark:text-[#000]">
                <div className="flex items-center">
                  <svg className="icon me-2 text-[24px]">
                    <use href="#icon_social_new"></use>
                  </svg>
                  Social
                </div>
              </Accordion.Title>
              <Accordion.Content className="contentAcco pt-0">
                <hr />
                <div className="pt-3">
                  <div className="grid grid-cols-5 gap-4 btnWrapper">
                    <div className="btnBox">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img src="images/x.svg" className="wh-40px" alt="..." />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Twitter</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      {!twitterProfile && (
                        <div
                          className="connectBox"
                          onClick={() => {
                            if (checking) {
                              return;
                            }
                            if (!twitterProfile) {
                              handleTwitterLogin();
                            }
                          }}
                        >
                          <div className="conConnect">Connect</div>
                        </div>
                      )}
                      {twitterProfile && (
                        <span className="checkTick">
                          <svg className="icon">
                            <use href="#icon_green-check"></use>
                          </svg>
                        </span>
                      )}
                    </div>

                    <div className="btnBox">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/linkedin.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>LinkedIn</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      {!localStorage.getItem("linkedin_access") && (
                        <div
                          className="connectBox"
                          onClick={() => integrateLinkedIn()}
                        >
                          <div className="conConnect">Connect</div>
                        </div>
                      )}
                      {localStorage.getItem("linkedin_access") && (
                        <span className="checkTick">
                          <svg className="icon">
                            <use href="#icon_green-check"></use>
                          </svg>
                        </span>
                      )}
                    </div>

                    {/* <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/fb.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Facebook</h6>

                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div>

                    <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/insta.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Instagram</h6>

                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div> */}
                  </div>
                </div>
              </Accordion.Content>
            </Accordion.Panel>
          </Accordion>
        </div>
      )}

      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
    </>
  );
}

export default IntegrationSocial;
